/**
 * Cliente REST do faturamento-api (integracao-faturamento): a tela Financeiro do Portal do
 * Gerador, pelas rotas `/billing/generator/*`.
 *
 * Chamada direta ao serviço com o token do próprio usuário (realm `generator`, ou `musa` para o
 * time interno). O escopo vem do generator-api (`GET /generators/me`), aplicado sobre o gerador
 * pagador do ciclo: ciclo fora do escopo, ou ainda em análise interna (RN-33), responde 404;
 * generator-api fora, 503. Parâmetro de query desconhecido responde 422 — só vai o que o
 * filtro declara.
 *
 * O serviço só devolve dado próprio (e Omie). Nome de material/embalagem, unidade, situação da
 * coleta e MTR são compostos no front — ver `src/services/invoiceDetails.ts`.
 */

import { getValidToken } from '@/integrations/keycloak/client';

const API_URL = import.meta.env.VITE_FATURAMENTO_API_URL;

/**
 * Estado do ciclo de faturamento. `CREATED` nunca chega ao gerador: ciclo ainda não fechado não é
 * visível nem contestável.
 */
export type ReleaseStatus = 'AWAITING_GENERATOR' | 'CONTESTED' | 'READY_TO_INVOICE' | 'CANCELLED';

export interface InvoiceFilterInput {
  page?: number;
  page_size?: number;
  due_date_from?: string;
  due_date_to?: string;
  billing_period_from?: string;
  billing_period_to?: string;
  /** Competências específicas (`YYYY-MM`), não necessariamente contíguas. */
  billing_periods?: string[];
  /**
   * Geradores escolhidos no filtro (qualquer nível da árvore). A API expande a subárvore no
   * servidor — a tela manda os ids selecionados, nunca a lista de filiais: um gerador com
   * centenas de lojas estourava a URL (414).
   */
  generator_id?: number[];
  /** Ids exatos do gerador pagador (`client.generator_id`), escolhidos à mão na tela. */
  establishment_ids?: number[];
  /** Trecho do documento do gerador pagador (só dígitos). */
  cnpj?: string;
  /** Status de alguma parcela na Omie (ex.: `A VENCER`). */
  status?: string;
  /** Inclui no extrato as medições em análise interna, ainda sem OS. O serviço assume `false`. */
  include_in_analysis?: boolean;
  /** Estado do ciclo — complementa `status`, que é o status na Omie. */
  release_status?: ReleaseStatus[];
  /** `'FATURA'` (tem OS na Omie) e/ou `'MEDICAO'` (em análise interna, sem OS). */
  types?: string[];
}

export interface PayingGenerator {
  id: number;
  name: string;
  document: string;
}

/** Uma NFS-e da OS (`omie.omie_nfse`). */
export interface NfItem {
  numero: string;
  valor: number;
  verificador: string;
  emissor: string;
  cod_nf: number;
}

/** Uma parcela do contas a receber da OS. `cod_titulo` é o `nCodTitulo` da rota de boleto. */
export interface CrParcela {
  parcela: string;
  cod_titulo: number;
  status: string;
  /** `YYYY-MM-DD`; nulo quando a Omie não informou vencimento. */
  vencimento: string | null;
  valor: number;
  documento_fiscal: string;
  boleto_gerado: 'S' | 'N' | null;
}

/** Linha do extrato (`GET /billing/generator/cycles`). */
export interface Invoice {
  /** Id do billing_cycle, como texto — o `:id` da rota `/financeiro/:id`. */
  sfm_id: string;
  /** Nulo enquanto a medição está em análise: a OS na Omie só existe depois do faturamento. */
  os_code?: string | null;
  numero_pedido?: string | null;
  /** Competência `YYYY-MM`. */
  billing_period?: string | null;
  value: number;
  /** `YYYY-MM-DD`. Nulo antes do faturamento. */
  due_date?: string | null;
  /** Status na Omie. Nulo enquanto não há contas a receber. */
  status?: string | null;
  release_status?: ReleaseStatus | null;
  /** Fim da janela de contestação. ISO 8601 com fuso. */
  release_at?: string | null;
  /** Início da janela de contestação (RN-01). ISO 8601 com fuso. */
  published_at?: string | null;
  /**
   * Pedido de Compra (EDD-582). `requires_po`: o cliente pagador só aceita nota com PO, então a
   * aprovação pede o número. `awaiting_po`: a janela venceu sem PO e a medição está travada até
   * alguém informar — derivado no backend, aqui só decide o selo.
   */
  po_number?: string | null;
  po_informed_by_name?: string | null;
  /** ISO 8601 com fuso. */
  po_informed_at?: string | null;
  requires_po?: boolean;
  awaiting_po?: boolean;
  is_fixed_value?: boolean;
  /** Vem de `faturamento.client`: id = `generator_id`, nome = `COALESCE(name, fantasy_name, legal_name)`. */
  paying_generator?: PayingGenerator | null;
  nf_data?: NfItem[] | null;
  cr_data?: CrParcela[] | null;
}

export interface Pagination {
  page: number;
  last_page: number;
  page_size: number;
  count: number;
}

export interface InvoicesResponse {
  data: Invoice[];
  pagination: Pagination;
}

/** Composição da fatura por natureza da regra. Nulo em fatura de valor fixo. */
export interface InvoiceBreakdown {
  credits: number;
  debits: number;
  rentals: number;
  total: number;
}

/**
 * Quem aprovou a medição, e quando.
 *
 * Ausente quando a liberação foi **automática** — o prazo de retenção venceu e a rotina diária
 * liberou, a medição nasceu sem janela, ou uma contestação foi resolvida. Ausente é resposta
 * válida: ninguém aprovou, e é essa diferença que a tela mostra.
 */
export interface MeasurementApproval {
  /** `generator` — o próprio cliente; `musa` — um colaborador. */
  realm: 'generator' | 'musa' | string | null;
  name: string | null;
  email: string | null;
  /** ISO 8601 com fuso. */
  approved_at: string;
}

/** `GET /billing/generator/cycles/{id}`: só dado do faturamento. */
export interface InvoiceDetailHeader {
  invoice: Invoice;
  /** Período do ciclo, `YYYY-MM-DD`. */
  period: { start: string; end: string } | null;
  is_fixed_value: boolean;
  /** Geradores (ids exatos) dos clientes da config — é por eles que a fatura fixa lista coletas no agreements. */
  generator_ids: number[];
  breakdown: InvoiceBreakdown | null;
  approval: MeasurementApproval | null;
}

/** Linha de `GET /billing/generator/cycles/{id}/collects`: só `transaction`/`event.payload`. */
export interface InvoiceCollectRow {
  /** Id da coleta no Hub (uuid). */
  collect_id: string;
  transaction_id: string | null;
  /** ISO 8601. */
  event_date: string;
  value: number;
  units: number | null;
  /** Peso em kg (nomenclatura do Engine). */
  units_collected: number | null;
  material_id: number | null;
  packaging_id: number | null;
  generator_id: number | null;
}

export interface InvoiceCollectRowsPage {
  data: InvoiceCollectRow[];
  pagination: Pagination;
  /** `agreements` = fatura de valor fixo: o faturamento não tem transação por coleta e devolve `data: []`. */
  source: 'faturamento' | 'agreements';
}

// ---- Shapes que a tela lê, compostos em src/services/invoiceDetails.ts ----

export interface InvoiceCollect {
  id: string;
  /**
   * Transação de faturamento gerada por esta coleta — é o objeto de uma contestação do tipo
   * "lançamento financeiro". Nulo em fatura de valor fixo, que não tem transação por coleta.
   */
  transaction_id?: string | null;
  /** Rótulos compostos de outros serviços; nulos quando nenhum deles conhece a coleta. */
  establishment: { id: number; name: string } | null;
  material: { id: number; name: string; unity: string } | null;
  packaging: { id: number; name: string } | null;
  /** `YYYY-MM-DD`. */
  planned_date: string;
  /** Estado da coleta no agreements: planned, collected, fault. Nulo quando ele não a conhece. */
  status: string | null;
  units: number | null;
  units_collected: number | null;
  weight: number | null;
  volume: number | null;
  value: number | null;
  /** `null` = mtr-manager indisponível; `[]` = sem MTR emitido. */
  mtr_numbers: string[] | null;
}

export interface InvoiceCollectsPage {
  data: InvoiceCollect[];
  pagination: Pagination;
}

/** Cabeçalho do detalhe, já normalizado para a tela. */
export interface InvoiceDetail {
  sfm_id: string;
  os_code: string | null;
  numero_pedido: string | null;
  billing_period: string;
  value: number;
  due_date: string | null;
  status: string | null;
  release_status: ReleaseStatus | null;
  release_at: string | null;
  published_at: string | null;
  po_number: string | null;
  po_informed_by_name: string | null;
  po_informed_at: string | null;
  requires_po: boolean;
  awaiting_po: boolean;
  is_fixed_value: boolean;
  paying_generator: PayingGenerator | null;
  nf_data: NfItem[];
  cr_data: CrParcela[];
}

export interface InvoiceDetailsFilterInput {
  id: string;
  page?: number;
  page_size?: number;
}

export interface InvoiceDetailsResponse {
  invoice: InvoiceDetail;
  breakdown?: InvoiceBreakdown | null;
  approval?: MeasurementApproval | null;
  collects: InvoiceCollectsPage;
}

export interface CollectCalcTerm {
  label: string;
  /** "780 kg × R$ 0,52/kg" — vazio para taxa fixa. */
  detail: string;
  value: number | null;
}

export interface CollectRuleRationale {
  /** Nome da regra. */
  label: string;
  summary: string;
  /** 'charge' | 'discount'. */
  direction: string;
  subtotal: number | null;
  translatable: boolean;
  terms: CollectCalcTerm[];
}

export interface CollectCalculation {
  /** Há transação/regra para esta coleta. */
  available: boolean;
  /** Valor oficial cobrado (Σ transaction_rule.value). */
  collect_total: number | null;
  /** true → exibir os termos; false → só summary + total (não reconcilia). */
  show_breakdown: boolean;
  material_id?: number | null;
  packaging_id?: number | null;
  generator_id?: number | null;
  rules: CollectRuleRationale[];
}

export class FaturamentoApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** `code` do corpo (`PO_REQUIRED`, `PO_INVALID`, `PO_DUPLICATE`…), quando o serviço manda. */
    readonly code: string | null = null,
    /** Medição que já usou a PO — vem só no `PO_DUPLICATE`. */
    readonly previous: DuplicatePoPrevious | null = null,
  ) {
    super(message);
    this.name = 'FaturamentoApiError';
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  /** Aprovação de medição que não está aguardando o gerador. */
  get isConflict(): boolean {
    return this.status === 409;
  }

  /** Aprovação barrada por contestação ativa. */
  get isLocked(): boolean {
    return this.status === 423;
  }

  /** A PO já foi informada em outra medição do grupo — o gerador decide seguir ou corrigir. */
  get isPoDuplicate(): boolean {
    return this.status === 409 && this.code === 'PO_DUPLICATE';
  }

  /** PO ausente ou fora do formato (1 a 50 caracteres). */
  get isPoInvalid(): boolean {
    return this.status === 422 && (this.code === 'PO_REQUIRED' || this.code === 'PO_INVALID');
  }
}

/** Medição anterior que usou a mesma PO (`409 PO_DUPLICATE`). Datas `YYYY-MM-DD`. */
export interface DuplicatePoPrevious {
  cycle_id: number;
  period_start: string;
  period_end: string;
  client_name: string;
}

/** FastAPI: `detail` é texto, ou a lista de erros de validação (`{ msg, loc }`). */
interface ApiErrorBody {
  detail?: string | Array<{ msg?: string }>;
  code?: string;
  previous?: DuplicatePoPrevious;
}

export function messageFromBody(body: ApiErrorBody | null, status: number): string {
  const detail = body?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) {
    const messages = detail.map((item) => item?.msg).filter((msg): msg is string => !!msg);
    if (messages.length) return messages.join(' ');
  }
  return `Erro ${status} no serviço de faturamento`;
}

type QueryValue = string | number | boolean | Array<string | number> | undefined | null;

/** Listas repetem a chave (`types=FATURA&types=MEDICAO`); nulo, vazio e lista vazia ficam de fora. */
export function queryString(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, String(item)));
    } else if (value != null && value !== '') {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

async function request<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T> {
  const token = await getValidToken();
  const hasBody = init?.body !== undefined;
  const res = await fetch(`${API_URL}${path}`, {
    method: init?.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(hasBody ? { body: JSON.stringify(init.body) } : {}),
  });

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      body = null;
    }
    throw new FaturamentoApiError(
      res.status,
      messageFromBody(body, res.status),
      body?.code ?? null,
      body?.previous ?? null,
    );
  }

  return (await res.json()) as T;
}

/** Linha como a API devolve: nome/documento do pagador podem vir nulos. */
type RawInvoice = Omit<Invoice, 'paying_generator'> & {
  paying_generator?: { id: number; name: string | null; document: string | null } | null;
};

/** A tela mascara e formata nome e CNPJ como texto; nulo vira vazio aqui, uma vez só. */
function normalizeInvoice(raw: RawInvoice): Invoice {
  return {
    ...raw,
    paying_generator: raw.paying_generator
      ? { id: raw.paying_generator.id, name: raw.paying_generator.name ?? '', document: raw.paying_generator.document ?? '' }
      : null,
  };
}

const cyclePath = (id: string) => `/billing/generator/cycles/${encodeURIComponent(id)}`;

/** Extrato: faturas e, com `include_in_analysis`, medições em análise. */
export async function fetchInvoices(filter: InvoiceFilterInput = {}): Promise<InvoicesResponse> {
  const result = await request<{ data: RawInvoice[]; pagination: Pagination }>(
    `/billing/generator/cycles${queryString({ ...filter })}`,
  );
  return { data: (result.data ?? []).map(normalizeInvoice), pagination: result.pagination };
}

interface RawInvoiceDetailHeader {
  invoice: RawInvoice;
  period?: { start: string; end: string } | null;
  is_fixed_value?: boolean;
  generator_ids?: number[] | null;
  breakdown?: InvoiceBreakdown | null;
  approval?: MeasurementApproval | null;
}

/** Cabeçalho do detalhe: fatura, período, geradores da config, composição e aprovação. */
export async function fetchInvoiceDetailHeader(id: string): Promise<InvoiceDetailHeader> {
  const raw = await request<RawInvoiceDetailHeader>(cyclePath(id));
  const invoice = normalizeInvoice(raw.invoice);
  return {
    invoice,
    period: raw.period ?? null,
    is_fixed_value: raw.is_fixed_value ?? invoice.is_fixed_value ?? false,
    generator_ids: raw.generator_ids ?? [],
    breakdown: raw.breakdown ?? null,
    approval: raw.approval ?? null,
  };
}

/** Linhas de transação do ciclo, paginadas (`page_size` até 100). Fatura fixa: `data: []`, `source: 'agreements'`. */
export async function fetchInvoiceCollectRows(id: string, page = 1, pageSize = 10): Promise<InvoiceCollectRowsPage> {
  const result = await request<InvoiceCollectRowsPage>(`${cyclePath(id)}/collects${queryString({ page, page_size: pageSize })}`);
  return { data: result.data ?? [], pagination: result.pagination, source: result.source ?? 'faturamento' };
}

/**
 * Quem aprovou a medição — em uma requisição **separada** da do detalhe, de propósito: a seção
 * "Histórico da medição" não pode derrubar a tela se esta leitura falhar.
 */
export async function fetchMeasurementApproval(id: string): Promise<MeasurementApproval | null> {
  const header = await fetchInvoiceDetailHeader(id);
  return header.approval ?? null;
}

/**
 * Aprova a medição: encerra a janela de contestação e libera o ciclo para o faturamento. O
 * autor é o dono do token. 409 quando a medição não está aguardando o gerador, 423 quando há
 * contestação ativa — a mensagem do serviço vai no erro. Com `po` (cliente que exige PO,
 * EDD-582): 422 `PO_REQUIRED`/`PO_INVALID` quando o número não serve e 409 `PO_DUPLICATE` quando
 * já foi usado no grupo — repetir com `confirm_duplicate` segue.
 */
export async function approveMeasurement(id: string, po?: ApproveMeasurementPo): Promise<boolean> {
  // Sem PO não vai corpo: cliente que não exige PO aprova como sempre aprovou.
  await request(`${cyclePath(id)}/approve`, {
    method: 'POST',
    body: po ? { po_number: po.po_number, confirm_duplicate: po.confirm_duplicate ?? false } : undefined,
  });
  return true;
}

/** PO informada na aprovação. `confirm_duplicate` reenvia depois do aviso de PO repetida. */
export interface ApproveMeasurementPo {
  po_number: string;
  confirm_duplicate?: boolean;
}

/** Link do boleto de uma parcela (`cod_titulo` de `cr_data`). `null` quando a Omie não tem boleto para o título. */
export async function fetchInvoiceBankSlipUrl(nCodTitulo: number): Promise<string | null> {
  try {
    const result = await request<{ url: string | null }>(`/billing/generator/titles/${nCodTitulo}/bank-slip`);
    return result.url ?? null;
  } catch (error) {
    if (error instanceof FaturamentoApiError && error.isNotFound) return null;
    throw error;
  }
}

/** Memória de cálculo de uma coleta da fatura (GR-497). */
export async function fetchInvoiceCollectCalculation(cycleId: string, collectId: string): Promise<CollectCalculation> {
  return request(`${cyclePath(cycleId)}/collects/${encodeURIComponent(collectId)}/calculation`);
}
